import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { log } from "../lib/log";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Modal, Chip } from "../components/ui-bits";
import DropZone from "../components/DropZone";
import { useAuth } from "../lib/auth";
import { useBrand } from "../lib/brand";
import { setActiveChatChannel } from "../lib/dmNotifier";
import {
  Send, Edit3, Trash2, Hash, MessageSquare, Users as UsersIcon, Loader2,
  Paperclip, X, Image as ImageIcon, File as FileIcon, Download, Plus,
  Search, Link2, MoreHorizontal, CornerUpLeft, ExternalLink, Package,
  Layers, Shirt, User, Truck, Palette, CalendarClock, FolderOpen, Calculator,
  ArrowDown,
} from "lucide-react";

const CHANNEL_ICON = { general: Hash, collection: MessageSquare, garment: MessageSquare, task: MessageSquare, custom: Hash, dm: User };
const HUB_KINDS = [
  { key: "garment",    label: "Capo",              endpoint: "/garments",     icon: Shirt,   textField: (x) => `${x.code || ""} — ${x.name || ""}` },
  { key: "collection", label: "Collezione",        endpoint: "/collections",  icon: Layers,  textField: (x) => x.name || "" },
  { key: "client",     label: "Cliente",           endpoint: "/clients",      icon: User,    textField: (x) => x.name || "" },
  { key: "order",      label: "Ordine",            endpoint: "/orders",       icon: Package, textField: (x) => x.external_order_id || (x.id || "").slice(0, 8) },
  { key: "material",   label: "Materiale",         endpoint: "/materials",    icon: Palette, textField: (x) => x.name || x.code || "" },
  { key: "supplier",   label: "Fornitore",         endpoint: "/suppliers",    icon: Truck,   textField: (x) => x.name || "" },
  { key: "task",       label: "Attività",          endpoint: "/tasks",        icon: CalendarClock, textField: (x) => x.title || "" },
  { key: "folder",     label: "Cartella",          endpoint: "/folders",      icon: FolderOpen, textField: (x) => x.name || "" },
  { key: "file",       label: "File",              endpoint: "/files",        icon: FileIcon, textField: (x) => x.original_filename || "" },
  { key: "simulation", label: "Preventivo",        endpoint: "/mms/simulations", icon: Calculator, textField: (x) => x.name || x.simulation_name || (x.id || "").slice(0, 8) },
];

const HUB_ICON = Object.fromEntries(HUB_KINDS.map(k => [k.key, k.icon]));

// Where each kind's deep link lives. Each function returns a URL that
// the target page reads via URLSearchParams to auto-open the item modal.
const HUB_DEEP_LINK = {
  garment:    (id) => `/collections-garments?tab=garments&open=${id}`,
  collection: (id) => `/collections-garments?tab=collections&open=${id}`,
  client:     (id) => `/clients-orders?tab=clients&open=${id}`,
  order:      (id) => `/clients-orders?tab=orders&open=${id}`,
  material:   (id) => `/materials-inventory?tab=materials&open=${id}`,
  supplier:   (id) => `/suppliers?open=${id}`,
  task:       (id) => `/tasks?open=${id}`,
  folder:     (id) => `/folder?folder=${id}`,
  file:       (id) => `/folder?file=${id}`,
  simulation: (id) => `/mms-simulator/${id}`,
};

const timeShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export default function ChatPage() {
  const { user } = useAuth();
  const { activeBrand } = useBrand();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [hubRefs, setHubRefs] = useState([]); // pending refs: {kind, id, title, subtitle}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [mentionOpen, setMentionOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [msgMenuOpen, setMsgMenuOpen] = useState(null);
  const [chanMenuOpen, setChanMenuOpen] = useState(null);
  const [hubPickerOpen, setHubPickerOpen] = useState(false);
  const [dmPickerOpen, setDmPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef();
  const prevMsgCountRef = useRef(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMsgPending, setNewMsgPending] = useState(0);

  const scrollToBottom = (behavior = "auto") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setNewMsgPending(0);
  };
  const nearBottom = (el, threshold = 100) => {
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  };
  const onScroll = () => {
    const at = nearBottom(scrollRef.current);
    setIsAtBottom(at);
    if (at) setNewMsgPending(0);
  };

  const loadChannels = async () => {
    try {
      const r = await api.get("/chat/channels");
      setChannels(r.data);
      if (!activeId && r.data[0]) setActiveId(r.data[0].id);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadMembers = async () => {
    if (!activeBrand?.id) return;
    try {
      const r = await api.get(`/brands/${activeBrand.id}/members`);
      setMembers(r.data);
    } catch (err) { log.warn("[chat] Failed to load members", err); }
  };
  const loadMessages = async () => {
    if (!activeId) return;
    try {
      const r = await api.get(`/chat/channels/${activeId}/messages`);
      setMessages(r.data.messages || []);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { loadChannels(); loadMembers(); }, [activeBrand?.id]);
  // On channel switch, reset scroll tracking and load messages.
  useEffect(() => {
    prevMsgCountRef.current = 0;
    setIsAtBottom(true);
    setNewMsgPending(0);
    setActiveChatChannel(activeId);
    loadMessages();
    return () => setActiveChatChannel(null);
  }, [activeId]);
  // After messages update: jump to bottom on channel open OR on new-msg when user
  // was already at the bottom. Otherwise keep scroll and show pending badge.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const prev = prevMsgCountRef.current;
    const curr = messages.length;
    if (curr === 0) { prevMsgCountRef.current = 0; return; }
    // Initial load for this channel — snap to the latest.
    if (prev === 0) {
      requestAnimationFrame(() => scrollToBottom("auto"));
    } else if (curr > prev) {
      const added = curr - prev;
      if (isAtBottom) {
        requestAnimationFrame(() => scrollToBottom("smooth"));
      } else {
        setNewMsgPending(n => n + added);
      }
    }
    prevMsgCountRef.current = curr;
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(loadMessages, 30000);
    const onMsg = (e) => {
      if (e.detail?.channel_id === activeId) loadMessages();
      loadChannels();
    };
    window.addEventListener("atelier:chat_message", onMsg);
    return () => { clearInterval(t); window.removeEventListener("atelier:chat_message", onMsg); };
  }, [activeId]);

  const send = async () => {
    if ((!text.trim() && attachments.length === 0 && hubRefs.length === 0) || !activeId) return;
    setBusy(true); setErr("");
    try {
      if (editing) {
        await api.patch(`/chat/messages/${editing.id}`, { text: text.trim() });
        setEditing(null);
      } else {
        await api.post(`/chat/channels/${activeId}/messages`, {
          text: text.trim() || (hubRefs.length ? "" : "(attachment)"),
          mentions,
          attachments: attachments.map(a => a.id),
          hub_refs: hubRefs.map(r => ({ kind: r.kind, id: r.id })),
          reply_to: replyTo?.id || null,
        });
      }
      setText(""); setMentions([]); setAttachments([]); setHubRefs([]); setReplyTo(null);
      setIsAtBottom(true);  // sender always wants to see their own new message
      await loadMessages();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadAttachmentFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true); setErr("");
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("linked_type", "chat_attachment");
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
        setAttachments(prev => [...prev, r.data]);
      } catch (er) { setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message); }
    }
    setUploading(false);
  };
  const uploadAttachments = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    return uploadAttachmentFiles(files);
  };
  const removeAttachment = (id) => setAttachments(prev => prev.filter(a => a.id !== id));
  const removeHubRef = (i) => setHubRefs(prev => prev.filter((_, idx) => idx !== i));

  const remove = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/chat/messages/${m.id}`); loadMessages(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const startEdit = (m) => { setEditing(m); setText(m.text); setReplyTo(null); };
  const cancelEdit = () => { setEditing(null); setText(""); };
  const startReply = (m) => { setReplyTo(m); setEditing(null); };
  const cancelReply = () => setReplyTo(null);

  // Channel actions
  const createChannel = async () => {
    const name = window.prompt("Nome del nuovo canale:");
    if (!name || !name.trim()) return;
    try {
      const r = await api.post("/chat/channels", { channel_type: "custom", name: name.trim() });
      await loadChannels();
      setActiveId(r.data.id);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // Open (or lazily create) a DM channel between the current user and `member`.
  const openDm = async (member) => {
    if (!member || member.user_id === user?.id) return;
    // Fast path — if the DM already exists in state, activate it.
    const existing = channels.find(
      c => c.channel_type === "dm"
        && (c.member_ids || []).includes(member.user_id)
        && (c.member_ids || []).includes(user?.id)
    );
    if (existing) { setActiveId(existing.id); return; }
    try {
      const r = await api.post("/chat/channels", { channel_type: "dm", member_id: member.user_id });
      await loadChannels();
      setActiveId(r.data.id);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const renameChannel = async (c) => {
    const name = window.prompt("Rinomina canale:", c.name);
    if (!name || name === c.name) return;
    try { await api.patch(`/chat/channels/${c.id}`, { name }); await loadChannels(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteChannel = async (c) => {
    if (!window.confirm(`Eliminare il canale "${c.name}"? I messaggi saranno rimossi.`)) return;
    try {
      await api.delete(`/chat/channels/${c.id}`);
      await loadChannels();
      if (activeId === c.id) setActiveId(null);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Mention @
  const onText = (v) => {
    setText(v);
    const at = v.lastIndexOf("@");
    setMentionOpen(at >= 0 && (at === v.length - 1 || /^@\w*$/.test(v.slice(at))));
  };
  const insertMention = (m) => {
    const at = text.lastIndexOf("@");
    setText(text.slice(0, at) + `@${m.name} `);
    setMentions([...new Set([...mentions, m.user_id])]);
    setMentionOpen(false);
  };

  const active = useMemo(() => channels.find(c => c.id === activeId), [channels, activeId]);
  const memberMap = useMemo(() => Object.fromEntries(members.map(m => [m.user_id, m])), [members]);
  const msgById = useMemo(() => Object.fromEntries(messages.map(m => [m.id, m])), [messages]);
  const filteredMessages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter(m => (m.text || "").toLowerCase().includes(q) || (m.sender_name || "").toLowerCase().includes(q));
  }, [messages, searchQuery]);

  return (
    <div data-testid="chat-page">
      <PageHeader eyebrow="Team" title="Brand Chat" description="Private conversations for this brand workspace only."/>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 h-[calc(100vh-260px)] min-h-[500px] overflow-hidden">
        {/* Channel sidebar */}
        <aside className="md:col-span-1 card-dark p-3 overflow-y-auto min-h-0" data-testid="chat-channels">
          <div className="flex items-center justify-between mb-2">
            <div className="label-caps">Canali</div>
            <button
              onClick={createChannel}
              className="text-[#525252] hover:text-[#0A0A0A] p-1"
              data-testid="new-channel-btn"
              title="Nuovo canale"
            >
              <Plus size={12}/>
            </button>
          </div>
          {channels.filter(c => c.channel_type !== "dm").map(c => {
            const Icon = CHANNEL_ICON[c.channel_type] || MessageSquare;
            return (
              <div
                key={c.id}
                className={`group relative flex items-center justify-between px-2 py-2 text-xs cursor-pointer ${activeId === c.id ? "bg-[#0A0A0A]/10" : "hover:bg-black/[0.03]"}`}
                data-testid={`chat-channel-${c.id}`}
              >
                <button onClick={()=>setActiveId(c.id)} className="flex items-center gap-2 min-w-0 flex-1 text-left text-[#0A0A0A]">
                  <Icon size={12}/>
                  <span className="truncate">{c.name}</span>
                </button>
                {c.unread > 0 && <span className="bg-[#0A0A0A] text-white text-[10px] px-1.5 py-0.5 min-w-[16px] text-center">{c.unread}</span>}
                <div className={`${chanMenuOpen === c.id ? "flex" : "hidden group-hover:flex"} items-center relative ml-1`}>
                  <button
                    onClick={(e)=>{ e.stopPropagation(); setChanMenuOpen(chanMenuOpen === c.id ? null : c.id); }}
                    className="text-[#525252] hover:text-[#0A0A0A] p-0.5"
                    data-testid={`chan-menu-btn-${c.id}`}
                  >
                    <MoreHorizontal size={12}/>
                  </button>
                  {chanMenuOpen === c.id && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={()=>setChanMenuOpen(null)}/>
                      <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] bg-white border border-[#E5E5E5] shadow-md py-1"
                        data-testid={`chan-menu-${c.id}`}
                        onClick={(e)=>e.stopPropagation()}>
                        <button onClick={()=>{ setChanMenuOpen(null); renameChannel(c); }}
                          className="w-full text-left px-3 py-1.5 text-xs text-[#0A0A0A] hover:bg-[#FAFAFA] flex items-center gap-2"
                          data-testid={`chan-menu-rename-${c.id}`}>
                          <Edit3 size={11}/> Rinomina
                        </button>
                        <button onClick={()=>{ setChanMenuOpen(null); deleteChannel(c); }}
                          className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                          data-testid={`chan-menu-delete-${c.id}`}>
                          <Trash2 size={11}/> Elimina
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {channels.length === 0 && <div className="text-xs text-[#737373] italic">Loading…</div>}

          {/* Direct Messages */}
          <div className="mt-5 pt-4 border-t border-[#E5E5E5]">
            <div className="flex items-center justify-between mb-2">
              <div className="label-caps">Messaggi Diretti</div>
              <button
                onClick={() => setDmPickerOpen(true)}
                className="text-[#525252] hover:text-[#0A0A0A] p-1"
                data-testid="new-dm-btn"
                title="Nuovo messaggio privato"
              >
                <Plus size={12}/>
              </button>
            </div>
            {(() => {
              // Only render members that already have a DM with the current user.
              const rows = members
                .filter(m => m.user_id !== user?.id)
                .map(m => {
                  const dm = channels.find(c => c.channel_type === "dm"
                    && (c.member_ids || []).includes(m.user_id)
                    && (c.member_ids || []).includes(user?.id));
                  return dm ? { m, dm } : null;
                })
                .filter(Boolean)
                // Most recent conversation first
                .sort((a, b) => {
                  const ta = a.dm.last_message?.created_at || a.dm.created_at || "";
                  const tb = b.dm.last_message?.created_at || b.dm.created_at || "";
                  return tb.localeCompare(ta);
                });
              if (rows.length === 0) {
                return <div className="text-[10px] text-[#737373] italic px-2 py-2">Nessuna conversazione. Clicca "+" per iniziarne una.</div>;
              }
              return rows.map(({ m, dm }) => {
                const memberName = m.user?.name || m.user?.email || m.name || m.email || "Membro";
                const memberEmail = m.user?.email || m.email;
                const isActive = activeId === dm.id;
                const unread = dm?.unread || 0;
                const lastText = dm?.last_message?.text;
                return (
                  <button
                    key={m.user_id}
                    onClick={() => openDm({ user_id: m.user_id, name: memberName, email: memberEmail })}
                    className={`w-full text-left flex items-start gap-2 px-2 py-2 text-xs ${isActive ? "bg-[#0A0A0A]/10" : "hover:bg-black/[0.03]"}`}
                    data-testid={`dm-item-${m.user_id}`}
                  >
                    <div className={`w-6 h-6 rounded-full border ${unread > 0 ? "border-[#0A0A0A]" : "border-[#E5E5E5]"} bg-white flex items-center justify-center text-[10px] font-medium text-[#0A0A0A] shrink-0`}>
                      {memberName.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`truncate ${unread > 0 ? "text-[#0A0A0A] font-medium" : "text-[#0A0A0A]"}`}>{memberName}</span>
                        {unread > 0 && <span className="bg-[#0A0A0A] text-white text-[10px] px-1.5 py-0.5 min-w-[16px] text-center" data-testid={`dm-unread-${m.user_id}`}>{unread}</span>}
                      </div>
                      <div className="text-[10px] text-[#737373] truncate">
                        {lastText || <span className="italic">nessun messaggio</span>}
                      </div>
                    </div>
                  </button>
                );
              });
            })()}
          </div>
        </aside>

        {/* Messages panel */}
        <section className="md:col-span-3 card-dark flex flex-col min-h-0 overflow-hidden" data-testid="chat-messages">
          <div className="border-b border-[#E5E5E5] p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {(() => {
                const H = CHANNEL_ICON[active?.channel_type] || Hash;
                return <H size={14} className="text-[#0A0A0A]"/>;
              })()}
              <div className="font-serif text-lg truncate">{active?.name || "—"}</div>
              {active?.channel_type === "dm" && <span className="text-[9px] uppercase tracking-widest text-[#737373] border border-[#E5E5E5] px-1.5 py-0.5">Privato</span>}
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#737373]"/>
                <input
                  placeholder="Cerca..."
                  value={searchQuery}
                  onChange={(e)=>setSearchQuery(e.target.value)}
                  className="input-dark pl-7 py-1 text-xs w-40"
                  data-testid="chat-search"
                />
              </div>
              <div className="text-[10px] text-[#737373] uppercase tracking-widest inline-flex items-center gap-2">
                <UsersIcon size={10}/> {members.length}
              </div>
            </div>
          </div>

          <DropZone
            onFiles={uploadAttachmentFiles}
            testId="chat-dropzone"
            className="flex-1 flex flex-col min-h-0 relative"
            overlayLabel="Drop to attach"
            accept="images, PDFs, up to 100MB each"
          >
          <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
            {filteredMessages.length === 0 && (
              <div className="text-center text-xs text-[#737373] italic py-8">
                {searchQuery ? "Nessun messaggio corrispondente." : "Nessun messaggio. Inizia la conversazione!"}
              </div>
            )}
            {filteredMessages.map(m => {
              const isMine = m.sender_id === user?.id;
              const replied = m.reply_to ? msgById[m.reply_to] : null;
              const isDm = active?.channel_type === "dm";
              const dmOther = isDm ? (active.member_ids || []).find(uid => uid !== user?.id) : null;
              const dmSeen = isDm && isMine && dmOther && (m.read_by || []).includes(dmOther);
              return (
                <div key={m.id} className={`group flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
                  <div className={`max-w-[75%] ${isMine ? "text-right" : "text-left"}`}>
                    <div className="text-[10px] text-[#737373] mb-1 uppercase tracking-widest">
                      {m.sender_name || m.sender_email} · {timeShort(m.created_at)} {m.edited_at && "(edited)"}
                      {isDm && isMine && (
                        <span
                          className={`ml-1 ${dmSeen ? "text-[#0A0A0A]" : "text-[#a3a3a3]"}`}
                          title={dmSeen ? "Letto" : "Non letto"}
                          data-testid={`dm-read-${m.id}`}
                        >
                          {dmSeen ? "✓✓" : "✓"}
                        </span>
                      )}
                    </div>
                    <div className={`inline-block p-3 text-sm text-left ${isMine ? "bg-[#0A0A0A]/10 text-[#0A0A0A]" : "bg-white border border-[#E5E5E5] text-[#0A0A0A]"}`}>
                      {replied && (
                        <div className="mb-2 pl-2 border-l-2 border-[#0A0A0A]/40 text-[11px] text-[#525252]">
                          <div className="text-[9px] uppercase tracking-widest">Rispondi a {replied.sender_name || replied.sender_email}</div>
                          <div className="truncate max-w-[300px]">{replied.text}</div>
                        </div>
                      )}
                      {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                      {(m.hub_refs || []).length > 0 && (
                        <div className="mt-2 space-y-2" data-testid={`chat-refs-${m.id}`}>
                          {m.hub_refs.map((r, i) => (
                            <HubRefCard key={`${r.kind}-${r.id}-${i}`} refItem={r} onOpen={(href) => navigate(href)}/>
                          ))}
                        </div>
                      )}
                      {(m.attachments || []).length > 0 && (
                        <div className="mt-2 space-y-2" data-testid={`chat-attachments-${m.id}`}>
                          {m.attachments.map(aid => (
                            <ChatAttachment key={aid} fileId={aid} fileUrl={fileUrl}/>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Message menu ⋯ */}
                    <div className={`mt-1 flex gap-2 ${isMine ? "justify-end" : "justify-start"} opacity-0 group-hover:opacity-100 transition-opacity relative`}>
                      <button onClick={()=>startReply(m)} className="text-[10px] text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1" data-testid={`msg-reply-${m.id}`}>
                        <CornerUpLeft size={10}/> Rispondi
                      </button>
                      <button
                        onClick={()=>setMsgMenuOpen(msgMenuOpen === m.id ? null : m.id)}
                        className="text-[10px] text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1"
                        data-testid={`msg-menu-btn-${m.id}`}
                      >
                        <MoreHorizontal size={10}/>
                      </button>
                      {msgMenuOpen === m.id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={()=>setMsgMenuOpen(null)}/>
                          <div className={`absolute z-50 min-w-[140px] bg-white border border-[#E5E5E5] shadow-md py-1 ${isMine ? "right-0" : "left-0"} top-full mt-1`}
                            data-testid={`msg-menu-${m.id}`}
                            onClick={(e)=>e.stopPropagation()}>
                            <button onClick={()=>{ setMsgMenuOpen(null); startReply(m); }}
                              className="w-full text-left px-3 py-1.5 text-xs text-[#0A0A0A] hover:bg-[#FAFAFA] flex items-center gap-2">
                              <CornerUpLeft size={11}/> Rispondi
                            </button>
                            <button onClick={()=>{ setMsgMenuOpen(null); navigator.clipboard?.writeText(m.text || "").catch(()=>{}); }}
                              className="w-full text-left px-3 py-1.5 text-xs text-[#0A0A0A] hover:bg-[#FAFAFA] flex items-center gap-2">
                              <Link2 size={11}/> Copia testo
                            </button>
                            {isMine && (
                              <>
                                <button onClick={()=>{ setMsgMenuOpen(null); startEdit(m); }}
                                  className="w-full text-left px-3 py-1.5 text-xs text-[#0A0A0A] hover:bg-[#FAFAFA] flex items-center gap-2"
                                  data-testid={`msg-menu-edit-${m.id}`}>
                                  <Edit3 size={11}/> Modifica
                                </button>
                                <button onClick={()=>{ setMsgMenuOpen(null); remove(m); }}
                                  className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  data-testid={`msg-menu-delete-${m.id}`}>
                                  <Trash2 size={11}/> Elimina
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Floating "new messages" pill — only when user scrolled up */}
          {!isAtBottom && newMsgPending > 0 && (
            <button
              onClick={() => scrollToBottom("smooth")}
              className="absolute left-1/2 -translate-x-1/2 bottom-24 z-10 bg-[#0A0A0A] text-white text-[11px] uppercase tracking-widest px-3 py-1.5 shadow-md hover:bg-[#141414] inline-flex items-center gap-2"
              data-testid="new-messages-pill"
            >
              <ArrowDown size={12}/> {newMsgPending} {newMsgPending === 1 ? "nuovo messaggio" : "nuovi messaggi"}
            </button>
          )}

          {err && <div className="p-2 text-xs text-red-600 border-t border-[#E5E5E5]">{err}</div>}
          {editing && (
            <div className="px-3 py-2 border-t border-[#E5E5E5] text-xs text-[#0A0A0A] flex items-center justify-between">
              <span>Modifica messaggio</span>
              <button onClick={cancelEdit} className="text-[#525252] hover:text-[#0A0A0A] text-[10px] uppercase tracking-widest">Annulla</button>
            </div>
          )}
          {replyTo && (
            <div className="px-3 py-2 border-t border-[#E5E5E5] text-xs text-[#0A0A0A] flex items-center justify-between bg-[#FAFAFA]" data-testid="reply-banner">
              <div className="flex items-center gap-2 min-w-0">
                <CornerUpLeft size={11}/>
                <span className="text-[#737373]">Risposta a <span className="text-[#0A0A0A]">{replyTo.sender_name || replyTo.sender_email}</span>:</span>
                <span className="truncate max-w-[300px] text-[#525252]">{replyTo.text}</span>
              </div>
              <button onClick={cancelReply} className="text-[#525252] hover:text-red-600"><X size={11}/></button>
            </div>
          )}

          <div className="p-3 border-t border-[#E5E5E5] relative">
            {(attachments.length > 0 || hubRefs.length > 0) && (
              <div className="mb-2 flex flex-wrap gap-2" data-testid="chat-pending">
                {attachments.map(a => (
                  <div key={a.id} className="inline-flex items-center gap-2 bg-white border border-[#E5E5E5] px-2 py-1 text-xs">
                    {a.content_type?.startsWith("image/") ? <ImageIcon size={12}/> : <FileIcon size={12}/>}
                    <span className="max-w-[160px] truncate">{a.original_filename}</span>
                    <button onClick={()=>removeAttachment(a.id)} className="text-[#525252] hover:text-red-600"><X size={12}/></button>
                  </div>
                ))}
                {hubRefs.map((r, i) => {
                  const Icon = HUB_ICON[r.kind] || Link2;
                  return (
                    <div key={`${r.kind}-${r.id}-${i}`} className="inline-flex items-center gap-2 bg-white border border-[#0A0A0A] px-2 py-1 text-xs" data-testid={`pending-hubref-${i}`}>
                      <Icon size={12}/>
                      <span className="max-w-[180px] truncate">{r.title}</span>
                      <button onClick={()=>removeHubRef(i)} className="text-[#525252] hover:text-red-600"><X size={12}/></button>
                    </div>
                  );
                })}
              </div>
            )}
            {mentionOpen && members.length > 0 && (
              <div className="absolute bottom-full left-3 mb-2 card-dark w-64 max-h-48 overflow-y-auto z-10">
                {members.filter(m => m.user_id !== user?.id).map(m => (
                  <button key={m.user_id} onClick={()=>insertMention(m)} className="w-full text-left px-3 py-2 text-xs hover:bg-black/[0.03] text-[#0A0A0A]">
                    <span className="text-[#0A0A0A]">@{m.name}</span> <span className="text-[#737373]">· {m.brand_role}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={()=>setHubPickerOpen(true)}
                disabled={!!editing}
                title="Allega da HUB"
                className="w-10 h-10 flex items-center justify-center border border-[#E5E5E5] hover:border-[#0A0A0A] text-[#525252] hover:text-[#0A0A0A] disabled:opacity-40"
                data-testid="chat-hub-attach"
              >
                <Plus size={14}/>
              </button>
              <button
                type="button"
                onClick={()=>fileRef.current?.click()}
                disabled={uploading || !!editing}
                title="Allega file"
                className="w-10 h-10 flex items-center justify-center border border-[#E5E5E5] hover:border-[#0A0A0A] text-[#525252] hover:text-[#0A0A0A] disabled:opacity-40"
                data-testid="chat-attach"
              >
                {uploading ? <Loader2 size={14} className="animate-spin"/> : <Paperclip size={14}/>}
              </button>
              <input ref={fileRef} type="file" hidden multiple onChange={uploadAttachments}/>
              <textarea
                data-testid="chat-input"
                rows={2} value={text}
                onChange={(e)=>onText(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={`Message #${active?.name || "channel"}… usa @ per menzionare`}
                className="input-dark flex-1 resize-none"
              />
              <button onClick={send} disabled={busy || (!text.trim() && attachments.length === 0 && hubRefs.length === 0)} className="btn-gold inline-flex items-center gap-2" data-testid="chat-send">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                {editing ? "Salva" : "Invia"}
              </button>
            </div>
            {mentions.length > 0 && (
              <div className="mt-2 text-[10px] text-[#0A0A0A]">
                Menzioni: {mentions.map(uid => memberMap[uid]?.name).filter(Boolean).join(", ")}
              </div>
            )}
          </div>
          </DropZone>
        </section>
      </div>

      <HubPickerModal
        open={hubPickerOpen}
        onClose={()=>setHubPickerOpen(false)}
        onPick={(item) => { setHubRefs(prev => [...prev, item]); setHubPickerOpen(false); }}
      />

      <DmPickerModal
        open={dmPickerOpen}
        onClose={()=>setDmPickerOpen(false)}
        members={members}
        currentUserId={user?.id}
        onPick={(m) => { setDmPickerOpen(false); openDm(m); }}
      />
    </div>
  );
}

// -------------------- DM Picker --------------------

function DmPickerModal({ open, onClose, members, currentUserId, onPick }) {
  const [q, setQ] = useState("");
  useEffect(() => { if (open) setQ(""); }, [open]);

  const others = (members || []).filter(m => m.user_id !== currentUserId);
  const query = q.trim().toLowerCase();
  const filtered = others.filter(m => {
    if (!query) return true;
    const name = (m.user?.name || m.user?.email || m.name || m.email || "").toLowerCase();
    const role = (m.brand_role || "").toLowerCase();
    return name.includes(query) || role.includes(query);
  });

  return (
    <Modal open={open} onClose={onClose} title="Nuovo messaggio privato">
      <div className="relative mb-3">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#737373]"/>
        <input
          autoFocus
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          placeholder="Cerca un membro del team..."
          className="input-dark pl-7 text-xs w-full"
          data-testid="dm-picker-search"
        />
      </div>
      <div className="max-h-[360px] overflow-y-auto" data-testid="dm-picker-results">
        {filtered.length === 0 ? (
          <div className="text-center text-xs text-[#737373] italic py-8">
            {others.length === 0 ? "Nessun altro membro nel brand." : "Nessun risultato."}
          </div>
        ) : (
          <div className="divide-y divide-[#F0F0F0]">
            {filtered.map(m => {
              const name = m.user?.name || m.user?.email || m.name || m.email || "Membro";
              const email = m.user?.email || m.email;
              const role = (m.brand_role || "").replaceAll("_", " ");
              return (
                <button
                  key={m.user_id}
                  onClick={() => onPick({ user_id: m.user_id, name, email })}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left text-xs hover:bg-[#FAFAFA] transition-colors"
                  data-testid={`dm-picker-user-${m.user_id}`}
                >
                  <div className="w-7 h-7 rounded-full border border-[#E5E5E5] bg-white flex items-center justify-center text-[11px] font-medium text-[#0A0A0A] shrink-0">
                    {name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0A0A0A] truncate">{name}</div>
                    <div className="text-[10px] text-[#737373] uppercase tracking-widest truncate">{role || "member"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}


// -------------------- HUB Picker --------------------

function HubPickerModal({ open, onClose, onPick }) {
  const [tab, setTab] = useState("garment");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setQ("");
    const cfg = HUB_KINDS.find(k => k.key === tab);
    if (!cfg) return;
    setLoading(true);
    api.get(cfg.endpoint)
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab, open]);

  const filtered = useMemo(() => {
    const cfg = HUB_KINDS.find(k => k.key === tab);
    if (!cfg) return [];
    const query = q.trim().toLowerCase();
    return items
      .filter(x => !x.status || x.status !== "Archiviato")
      .filter(x => {
        if (!query) return true;
        return (cfg.textField(x) || "").toLowerCase().includes(query);
      })
      .slice(0, 100);
  }, [items, q, tab]);

  const pick = (x) => {
    const cfg = HUB_KINDS.find(k => k.key === tab);
    const title = cfg.textField(x).trim();
    onPick({ kind: tab, id: x.id, title });
  };

  return (
    <Modal open={open} onClose={onClose} title="Allega dal HUB" wide>
      <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-[#E5E5E5]" data-testid="hub-picker-tabs">
        {HUB_KINDS.map(k => {
          const Icon = k.icon;
          return (
            <button
              key={k.key}
              onClick={()=>setTab(k.key)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest inline-flex items-center gap-1.5 border transition-colors ${tab === k.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "text-[#525252] border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
              data-testid={`hub-tab-${k.key}`}
            >
              <Icon size={12}/> {k.label}
            </button>
          );
        })}
      </div>
      <div className="relative mb-3">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#737373]"/>
        <input
          autoFocus
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          placeholder="Cerca..."
          className="input-dark pl-7 text-xs w-full"
          data-testid="hub-picker-search"
        />
      </div>
      <div className="max-h-[420px] overflow-y-auto" data-testid="hub-picker-results">
        {loading ? (
          <div className="text-center text-xs text-[#737373] py-8"><Loader2 size={14} className="animate-spin inline"/> Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-xs text-[#737373] italic py-8">Nessun risultato.</div>
        ) : (
          <div className="divide-y divide-[#F0F0F0]">
            {filtered.map(x => {
              const cfg = HUB_KINDS.find(k => k.key === tab);
              const label = cfg.textField(x).trim();
              const Icon = cfg.icon;
              return (
                <button key={x.id} onClick={()=>pick(x)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left text-xs hover:bg-[#FAFAFA] transition-colors"
                  data-testid={`hub-pick-${tab}-${x.id}`}>
                  <Icon size={13} className="text-[#525252]"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0A0A0A] truncate">{label || "—"}</div>
                    <div className="text-[10px] text-[#737373] truncate">
                      {x.status || x.category || x.season || x.email || x.city || ""}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

// -------------------- Hub Ref Card (renders inside a message) --------------------

function HubRefCard({ refItem, onOpen }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setErr(false); setData(null);
    api.get(`/chat/hub-ref?kind=${refItem.kind}&id=${refItem.id}`)
      .then(r => setData(r.data))
      .catch(() => setErr(true));
  }, [refItem.kind, refItem.id]);

  const Icon = HUB_ICON[refItem.kind] || Link2;
  const kindLabel = HUB_KINDS.find(k => k.key === refItem.kind)?.label || refItem.kind;

  if (err) {
    return (
      <div className="border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-[11px] text-[#737373] italic" data-testid={`hub-ref-missing`}>
        <Icon size={11} className="inline mr-1"/> {kindLabel} non più disponibile.
      </div>
    );
  }
  if (!data) {
    return (
      <div className="border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-[11px] text-[#737373]">
        <Loader2 size={11} className="inline mr-1 animate-spin"/> Caricamento…
      </div>
    );
  }
  const isImageFile = data.kind === "file" && (data.content_type || "").startsWith("image/");
  return (
    <button
      onClick={() => {
        const buildLink = HUB_DEEP_LINK[data.kind];
        const href = buildLink ? buildLink(data.id) : (data.open_href || "/dashboard");
        onOpen(href);
      }}
      className="block text-left w-full border border-[#E5E5E5] bg-white hover:border-[#0A0A0A] transition-colors overflow-hidden"
      data-testid={`hub-ref-card-${data.kind}-${data.id}`}
    >
      {isImageFile && (
        <div className="w-full bg-[#FAFAFA] flex items-center justify-center border-b border-[#F0F0F0]" data-testid={`hub-ref-thumb-${data.id}`}>
          <img
            src={fileUrl(data.id, "thumb")}
            alt={data.title || "file"}
            className="max-h-40 max-w-full object-contain"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}
      <div className="px-3 py-2">
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 flex items-center justify-center border border-[#0A0A0A] bg-white shrink-0">
            <Icon size={12}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#737373]">{kindLabel}</div>
            <div className="text-sm text-[#0A0A0A] truncate">{data.title || "—"}</div>
            {data.subtitle && <div className="text-[11px] text-[#525252] truncate">{data.subtitle}</div>}
            {data.status && (
              <div className="mt-1"><Chip>{data.status}</Chip></div>
            )}
          </div>
          <ExternalLink size={11} className="text-[#525252] mt-1"/>
        </div>
        <div className="mt-2 pt-2 border-t border-[#F0F0F0] text-[10px] uppercase tracking-widest text-[#0A0A0A]">
          Apri {kindLabel.toLowerCase()} →
        </div>
      </div>
    </button>
  );
}

// -------------------- Existing File Attachment --------------------

function ChatAttachment({ fileId, fileUrl }) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get(`/files/${fileId}/meta`)
      .then(r => setMeta(r.data))
      .catch((err) => { log.warn("[chat] Attachment meta lookup failed", err); });
  }, [fileId]);

  const url = fileUrl(fileId);
  const ct = meta?.content_type || "";
  const name = meta?.original_filename || "attachment";
  if (ct.startsWith("image/")) {
    const thumb = fileUrl(fileId, "thumb");
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block max-w-[240px]">
        <img src={thumb} alt={name} className="max-h-40 border border-[#E5E5E5]" loading="lazy"/>
      </a>
    );
  }
  return (
    <a href={url} download={name} className="inline-flex items-center gap-2 border border-[#E5E5E5] px-2 py-1 text-xs hover:border-[#0A0A0A]" data-testid={`chat-file-${fileId}`}>
      <FileIcon size={12} className="text-[#0A0A0A]"/>
      <span className="truncate max-w-[200px]">{name}</span>
      <Download size={12} className="text-[#525252]"/>
    </a>
  );
}
